import { useState, useRef, useCallback, useLayoutEffect, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

interface TargetColumnTooltipProps {
  description?: string;
  children: ReactNode;
}

/**
 * Wraps target column content with a hover tooltip showing the field description.
 * Renders via portal to avoid overflow clipping.
 */
export function TargetColumnTooltip({ description, children }: TargetColumnTooltipProps) {
  const [showTip, setShowTip] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  useLayoutEffect(() => {
    if (showTip) updatePosition();
  }, [showTip, updatePosition]);

  useEffect(() => {
    if (!showTip) return;
    const handleScrollResize = () => updatePosition();
    window.addEventListener("scroll", handleScrollResize, true);
    window.addEventListener("resize", handleScrollResize);
    return () => {
      window.removeEventListener("scroll", handleScrollResize, true);
      window.removeEventListener("resize", handleScrollResize);
    };
  }, [showTip, updatePosition]);

  if (!description) {
    return <>{children}</>;
  }

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      className="min-w-0"
    >
      {children}

      {createPortal(
        <AnimatePresence>
          {showTip && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.12 }}
              className="fixed z-[9999] w-60 p-3 rounded-lg bg-[var(--color-nav-bg)] text-white text-xs shadow-xl pointer-events-none"
              style={{
                top: pos.top,
                left: pos.left,
                transform: "translate(-50%, -100%)",
              }}
            >
              <p className="text-[var(--color-nav-text)] leading-relaxed">{description}</p>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-[var(--color-nav-bg)]" />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
